import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  AccountBalanceWallet,
  CreditCard,
  Smartphone,
  AttachMoney,
  CheckCircle,
} from "@mui/icons-material";

const PaymentConfirmationDialog = ({
  open,
  onClose,
  orderData,
  paymentType,
  onPaymentProcessed,
  currentUserId,
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");

  const userOrders = orderData?.groupOrder?.userOrders || [];
  const upfrontPercentage = orderData?.paymentSettings?.upfrontPercentage || 20;

  // Calculate payment amounts based on strategy
  const calculatePaymentAmounts = () => {
    const currentUserOrder = userOrders.find(order => order.userId === currentUserId);
    if (!currentUserOrder) return { amount: 0, description: "" };

    const baseAmount = ((currentUserOrder.subtotal || 0) * upfrontPercentage) / 100;
    
    switch (paymentType) {
      case "split_per_person":
        return {
          amount: baseAmount,
          description: "You pay only for your own items",
        };
      case "split_evenly":
        // Calculate split evenly amount
        const totalUpfront = userOrders.reduce((sum, order) => 
          sum + (((order.subtotal || 0) * upfrontPercentage) / 100), 0
        );
        const splitAmount = userOrders.length > 0 ? totalUpfront / userOrders.length : 0;
        return {
          amount: splitAmount,
          description: "Split evenly among all participants",
        };
      case "pay_for_all":
        // Calculate total amount only for users who haven't paid upfront
        const unpaidUsers = userOrders.filter(user => 
          user.amountPaid === 0 || user.paymentStatus !== "upfront_paid"
        );
        const unpaidSubtotal = unpaidUsers.reduce((sum, user) => sum + (user.subtotal || 0), 0);
        const totalAmount = (unpaidSubtotal * upfrontPercentage) / 100;
        return {
          amount: totalAmount,
          description: `You pay for ${unpaidUsers.length} participants (excluding those who already paid)`,
        };
      default:
        return {
          amount: baseAmount,
          description: "Standard payment",
        };
    }
  };

  const paymentInfo = calculatePaymentAmounts();

  const handlePaymentMethodChange = (event) => {
    setSelectedPaymentMethod(event.target.value);
  };

  const handleProcessPayment = async () => {
    if (!selectedPaymentMethod) return;

    setIsProcessing(true);
    setPaymentStatus("processing");

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Call the payment processing handler
      if (onPaymentProcessed) {
        await onPaymentProcessed({
          orderId: orderData._id,
          userId: currentUserId,
          amount: paymentInfo.amount,
          paymentMethod: selectedPaymentMethod,
          paymentType: paymentType,
        });
      }

      setPaymentStatus("success");
      
      // Close dialog after success
      setTimeout(() => {
        onClose();
        setPaymentStatus("");
        setSelectedPaymentMethod("");
      }, 2000);

    } catch (error) {
      console.error("Error processing payment:", error);
      setPaymentStatus("error");
    } finally {
      setIsProcessing(false);
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case "card":
        return <CreditCard />;
      case "wallet":
        return <Smartphone />;
      case "cash":
        return <AttachMoney />;
      default:
        return <AccountBalanceWallet />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "card":
        return "Credit/Debit Card";
      case "wallet":
        return "Digital Wallet";
      case "cash":
        return "Cash Payment";
      default:
        return "Unknown Method";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 1,
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
          color: "white",
          fontWeight: 700,
          fontSize: "1.5rem",
        }}
      >
        Payment Confirmation
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Payment status alerts */}
        {paymentStatus === "processing" && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Processing your payment...
          </Alert>
        )}
        {paymentStatus === "success" && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Payment completed! Your order has been confirmed.
          </Alert>
        )}
        {paymentStatus === "error" && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Payment failed. Please try again.
          </Alert>
        )}

        {/* Payment summary */}
        <Card sx={{ mb: 3, backgroundColor: "#F8FAFC" }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Payment Summary
            </Typography>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="body2">Payment Type:</Typography>
              <Chip
                label={paymentType?.replace(/_/g, " ").toUpperCase()}
                color="primary"
                size="small"
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2">Description:</Typography>
              <Typography variant="body2" color="text.secondary">
                {paymentInfo.description}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2">Upfront Percentage:</Typography>
              <Typography variant="body2" color="text.secondary">
                {upfrontPercentage}%
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total Amount:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#7C3AED" }}>
                ${paymentInfo.amount.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Order details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Order Details
            </Typography>
            <List dense>
              {userOrders.map((userOrder, index) => (
                <ListItem key={userOrder.userId} sx={{ px: 0 }}>
                  <ListItemText
                    primary={userOrder.userFullName}
                    secondary={`${userOrder.totalItems} items • $${userOrder.subtotal.toFixed(2)}`}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ${((userOrder.subtotal * upfrontPercentage) / 100).toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Payment method selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Payment Method</InputLabel>
          <Select
            value={selectedPaymentMethod}
            onChange={handlePaymentMethodChange}
            label="Payment Method"
            disabled={isProcessing || paymentStatus === "success"}
          >
            <MenuItem value="card">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCard />
                <Typography>Credit/Debit Card</Typography>
              </Box>
            </MenuItem>
            <MenuItem value="wallet">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Smartphone />
                <Typography>Digital Wallet</Typography>
              </Box>
            </MenuItem>
            <MenuItem value="cash">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AttachMoney />
                <Typography>Cash Payment</Typography>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ mr: 2, textTransform: "none" }}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          onClick={handleProcessPayment}
          variant="contained"
          disabled={!selectedPaymentMethod || isProcessing || paymentStatus === "success"}
          startIcon={paymentStatus === "success" ? <CheckCircle /> : <AccountBalanceWallet />}
          sx={{
            textTransform: "none",
            background: paymentStatus === "success" 
              ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
              : "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
            "&:hover": {
              background: paymentStatus === "success"
                ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                : "linear-gradient(135deg, #6D28D9 0%, #9333EA 100%)",
            },
          }}
        >
          {isProcessing 
            ? "Processing..." 
            : paymentStatus === "success" 
              ? "Payment Complete" 
              : "Pay Now"
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentConfirmationDialog;
